/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { qreq } from "../../../shared/qrequest";
import AppActions from "../../AppActions";
import BaseCom from "../../BaseCom";
import Card from "../../Card";
import FormInput from "../../FormInput";
import Icon from "../../Icon";
import L from "../../Lang";
import LoadingAlert from "../../LoadingAlert";
import { withParamsAndNavigate } from "../../WithParamsAndNavigate";

class RuleManagerEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            info: {
                conditions: [],
                actions: []
            }
        };
    }

    async componentDidMount() {
        this.applyThemeColors();
        await this.getSettings();
        await this.fillListAsync('/api/rule/fields', 'fields');
        await this.fillListAsync('/api/rule/modes', 'modes');
        await this.fillListAsync('/api/account/list', 'accounts');
        await this.fillListAsync('/api/target/list', 'targets');
        await this.fillListAsync('/api/rule/stages', 'stages');
        if (this.props.params.id)
            await this.load(this.props.params.id);
        else
            this.setState({ info: { conditions: [], actions: [], priority: 0 }, loaded: true });
    }

    load = async (id) => {
        try {
            let j = await qreq.getAsync(`/api/rule/${id}`);
            if (j.item)
                this.setState({ info: j.item, loaded: true });
            else {
                this.alert(j.errorMessage);
                this.props.navigate('/rule/list');
            }
        }
        catch (e) {
            this.alert(e);
            this.props.navigate('/rule/list');
        }
    }

    submit = async (e) => {
        e?.preventDefault();
        try {
            let j = await qreq.postAsync('/api/rule/save', this.state.info);
            if (j.errorCode === 0) {
                this.success('Rule saved successfully');
                this.props.navigate('/rule/list');
            }
            else {
                this.alert(j.errorMessage);
            }
        }
        catch (e) {
            console.error(e);
            this.unkownErrorCallback();
        }
    }

    addCond = () => {
        let conditions = [...this.state.info.conditions];
        conditions.push({});
        this.setState({ info: { ...this.state.info, conditions } });
    }

    removeCond = (cond) => {
        let conditions = [...this.state.info.conditions];
        conditions.splice(conditions.indexOf(cond), 1);
        this.setState({ info: { ...this.state.info, conditions } });
    }

    addAct = () => {
        let actions = [...this.state.info.actions];
        actions.push({});
        this.setState({ info: { ...this.state.info, actions } });
    }

    removeAct = (act) => {
        let actions = [...this.state.info.actions];
        actions.splice(actions.indexOf(act), 1);
        this.setState({ info: { ...this.state.info, actions } });
    }

    change = (e) => {
        this.setState({ info: { ...this.state.info } });
    }

    fieldChange = (cond) => {
        delete cond.value;
        delete cond.operator;
        delete cond.subField;
        this.change();
    }

    getOperators = (cond) => {
        var field = this.state.fields.find(f => f.id === cond.field);
        if (!field) return [];

        let list = [];
        field.operators.forEach(i => {
            list.push({ id: i, name: i });
        });
        return list;
    }

    selectAccount = (e, cond) => {
        cond.value = e.target.value;
        this.change();
    }

    selectTarget = (e, cond) => {
        cond.value = e.target.value;
        this.change();
    }

    render() {
        if (!this.state.loaded)
            return <LoadingAlert />;
        return (
            <div className="container-fluid">
                <h1><L>Rule Manager Edit</L></h1>
                <form onSubmit={this.submit}>
                    <Card title="Rule Details">
                        <FormInput model={this.state.info} name="name" label="Rule Name" onChange={this.change} required />
                        <FormInput model={this.state.info} name="ruleStageName" label="Execution Stage" type="select" options={this.state.stages} onChange={this.change} required />
                        <FormInput model={this.state.info} name="priority" label="Priority" type="number" onChange={this.change} placeholder="0" />
                        <FormInput model={this.state.info} name="description" label="Rule Description" type="textarea" onChange={this.change} />
                    </Card>
                    <Card title="Conditions">
                        {this.state.info.conditions?.map((cond, idx) => <div key={idx} className="d-flex gap-1 align-items-center">
                            <FormInput model={cond} name="field" label="Field" labelAsPlaceholder onChange={this.change} type="select" options={this.state.fields ?? []} required />
                            {cond.field && <>
                                {cond.field === 'result' && <div className="input-group mb-3">
                                    <select className="form-select" onChange={(e) => this.selectTarget(e, cond)} defaultValue={cond.value}>
                                        <option value=""><L>Select Target</L></option>
                                        {this.state.targets?.map((acc, idx) => <option key={idx} value={acc.id}>{acc.name} ({acc.code})</option>)}
                                    </select>
                                </div>}
                                <FormInput model={cond} name="operator" label="Operator" type="select" options={this.getOperators(cond)} labelAsPlaceholder onChange={this.change} required />
                                {cond.field === 'account' ? <div className="input-group mb-3">
                                    <select className="form-select" onChange={(e) => this.selectAccount(e, cond)} defaultValue={cond.value}>
                                        <option value=""><L>Select Account</L></option>
                                        {this.state.accounts?.map((acc, idx) => <option key={idx} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                    : <FormInput model={cond} name="value" label="Value" labelAsPlaceholder onChange={this.change} required />}
                            </>}
                            <div className="mb-3">
                                <button type="button" className="btn btn-danger d-block" onClick={() => this.removeCond(cond)}><Icon icon="faTrash" /></button>
                            </div>
                        </div>)}
                    </Card>
                    <Card title="Actions">
                        {this.state.info.actions?.map((action, idx) => <div key={idx} className="d-flex gap-1 align-items-center">
                            <FormInput model={action} name="mode" label="Action" labelAsPlaceholder onChange={this.change} type="select" options={this.state.modes ?? []} />
                            {action.mode === 'set-result' && <div className="input-group mb-3">
                                <select className="form-select" onChange={(e) => this.selectTarget(e, action)} defaultValue={action.value}>
                                    <option value=""><L>Select Target</L></option>
                                    {this.state.targets?.map((acc, idx) => <option key={idx} value={acc.id}>{acc.name} ({acc.code})</option>)}
                                </select>
                            </div>}
                            <FormInput model={action} name="value" label="Value" labelAsPlaceholder onChange={this.change} />
                            <div className="mb-3">
                                <button type="button" className="btn btn-danger d-block" onClick={() => this.removeAct(action)}><Icon icon="faTrash" /></button>
                            </div>
                        </div>)}
                    </Card>
                    <AppActions>
                        <button type="button" className="btn btn-secondary btn-lg text-dark" onClick={this.addCond} style={{ backgroundColor: '#FFA500' }}><L>Add Condition</L></button>
                        <button type="button" className="btn btn-secondary btn-lg text-dark" onClick={this.addAct} style={{ backgroundColor: '#32CD32' }}><L>Add Action</L></button>
                        <button type="submit" className="btn btn-primary btn-lg"><Icon icon="faSave" /> <L>Save</L></button>
                    </AppActions>
                </form>
            </div>
        );
    }
}

export default withParamsAndNavigate(RuleManagerEdit);